import config from "config/config";
import type { LanguageCode } from "config/languages";
import type { RegularPageName, RegularPageUrls, StoryblokPageName } from "./types";

export const languagesWithoutMagnitudePages: LanguageCode[] = ["ro-md"];

export const storyblokPages: Record<StoryblokPageName, number> = {
  airPassengerRights: 71636182,
  affiliate: 86291623,
  help: 90578664,
  privacy: 52663019,
  terms: 43841167,
  referralTerms: 59199425,
  affiliateTerms: 71636190,
  affiliatePrivacy: 77511577,
  airhelpPlus: 282180211,
  airPayout: 234864063,
  helpAFriend: 64335827,
  airhelpReviews: 71636194,
  ourFees: 142848538,
  aboutUs: 61788293,
  press: 62392695,
  legalPartners: 71112922,
  becomeAPartner: 68898974,
  forAirlines: 70480540,
  cookies: 44293804,
  planYourTrip: 279912103,
};

export const regularPages: Record<RegularPageName, RegularPageUrls> = {
  cookies: {
    fallback: "cookies",
    fi: "evasteet",
    nb: "informasjonskapsler",
  },
  airlineStrikeCompensation: {
    da: "strike/kompensation-for-flystrejke",
    de: "strike/entschaedigung-bei-streik-der-fluggesellschaft",
    el: "strike/αποζημίωση-σε-περίπτωση-απεργίας-αερ",
    en: "strike/airline-strike-compensation",
    es: "strike/compensacion-por-huelga-de-aerolinea",
    fi: "strike/korvaukset-lentolakoista",
    fr: "strike/indemnite-de-greve-des-compagnies-aeriennes",
    it: "strike/risarcimento-per-sciopero-della-compagnia-aerea",
    nb: "strike/kompensasjon-ved-flystreik",
    nl: "strike/compensatie-voor-staking-van-de-luchtvaartmaatschappij",
    pl: "strike/odszkodowanie-za-lot-zaklocony-wskutek-strajku-linii-lotniczej",
    "pt-br": "strike/indenizacao-greve-companhias-aereas",
    "pt-pt": "strike/compensacao-por-greve-da-companhia-aerea",
    ro: "strike/despagubiri-datorate-pentru-greva-la-compania-aeriana",
    ru: "strike/компенсация-в-случае-забастовки-рабо",
    sv: "strike/ersattning-for-flygstrejk",
    isMagnitude: true,
  },
  airlines: {
    da: "airlines",
    de: "airlines",
    el: "airlines",
    en: "airlines",
    es: "airlines",
    fi: "airlines",
    it: "airlines",
    fr: "airlines",
    nb: "airlines",
    nl: "airlines",
    pl: "airlines",
    pt: "airlines",
    ro: "airlines",
    ru: "airlines",
    sv: "airlines",
    isMagnitude: true,
  },

  airports: {
    da: "airports",
    de: "airports",
    el: "airports",
    en: "airports",
    es: "airports",
    fi: "airports",
    it: "airports",
    fr: "airports",
    nb: "airports",
    nl: "airports",
    pl: "airports",
    pt: "airports",
    ro: "airports",
    ru: "airports",
    sv: "airports",
    isMagnitude: true,
  },
  webapp: {
    isExternal: true,
    bg: `${config.webappUrl}?lang=bg`,
    cs: `${config.webappUrl}?lang=cs`,
    da: `${config.webappUrl}?lang=da`,
    de: `${config.webappUrl}?lang=de`,
    el: `${config.webappUrl}?lang=el`,
    en: `${config.webappUrl}?lang=en`,
    es: `${config.webappUrl}?lang=es`,
    fi: `${config.webappUrl}?lang=fi`,
    fr: `${config.webappUrl}?lang=fr`,
    hu: `${config.webappUrl}?lang=hu`,
    it: `${config.webappUrl}?lang=it`,
    nb: `${config.webappUrl}?lang=nb`,
    nl: `${config.webappUrl}?lang=nl`,
    pl: `${config.webappUrl}?lang=pl`,
    "pt-br": `${config.webappUrl}?lang=pt-BR`,
    "pt-pt": `${config.webappUrl}?lang=pt`,
    ro: `${config.webappUrl}?lang=ro`,
    ru: `${config.webappUrl}?lang=ru`,
    sv: `${config.webappUrl}?lang=sv`,
    "zh-cn": `${config.webappUrl}?lang=zh-CN`,
    // tr: `${config.webappUrl}?lang=tr`,
    fallback: `${config.webappUrl}?lang=en`,
  },
  webappNewClaim: {
    isExternal: true,
    bg: `${config.funnelUrl}/claims/new?lang=bg`,
    cs: `${config.funnelUrl}/claims/new?lang=cs`,
    da: `${config.funnelUrl}/claims/new?lang=da`,
    de: `${config.funnelUrl}/claims/new?lang=de`,
    "de-at": `${config.funnelUrl}/claims/new?lang=de-at`,
    "de-ch": `${config.funnelUrl}/claims/new?lang=de-ch`,
    el: `${config.funnelUrl}/claims/new?lang=el`,
    en: `${config.funnelUrl}/claims/new?lang=en`,
    "en-au": `${config.funnelUrl}/claims/new?lang=en-au`,
    "en-ca": `${config.funnelUrl}/claims/new?lang=en-ca`,
    "en-gb": `${config.funnelUrl}/claims/new?lang=en-gb`,
    "en-in": `${config.funnelUrl}/claims/new?lang=en-in`,
    "en-int": `${config.funnelUrl}/claims/new?lang=en-int`,
    "en-ie": `${config.funnelUrl}/claims/new?lang=en-ie`,
    "en-nz": `${config.funnelUrl}/claims/new?lang=en-nz`,
    fi: `${config.funnelUrl}/claims/new?lang=fi`,
    fr: `${config.funnelUrl}/claims/new?lang=fr`,
    "fr-be": `${config.funnelUrl}/claims/new?lang=fr-be`,
    "fr-ca": `${config.funnelUrl}/claims/new?lang=fr-ca`,
    hu: `${config.funnelUrl}/claims/new?lang=hu`,
    it: `${config.funnelUrl}/claims/new?lang=it`,
    nb: `${config.funnelUrl}/claims/new?lang=nb`,
    nl: `${config.funnelUrl}/claims/new?lang=nl`,
    pl: `${config.funnelUrl}/claims/new?lang=pl`,
    "pt-br": `${config.funnelUrl}/claims/new?lang=pt-BR`,
    "pt-pt": `${config.funnelUrl}/claims/new?lang=pt`,
    ro: `${config.funnelUrl}/claims/new?lang=ro`,
    "ro-md": `${config.funnelUrl}/claims/new?lang=ro-md`,
    ru: `${config.funnelUrl}/claims/new?lang=ru`,
    sv: `${config.funnelUrl}/claims/new?lang=sv`,
    "zh-cn": `${config.funnelUrl}/claims/new?lang=zh-CN`,
    es: `${config.funnelUrl}/claims/new?lang=es`,
    // tr: `${config.funnelUrl}/claims/new?lang=tr`,
    fallback: `${config.funnelUrl}/claims/new?lang=en`,
  },

  webappBarcodeScanner: {
    isExternal: true,
    bg: `${config.funnelUrl}/claims/barcode/scanner?lang=bg`,
    cs: `${config.funnelUrl}/claims/barcode/scanner?lang=cs`,
    da: `${config.funnelUrl}/claims/barcode/scanner?lang=da`,
    de: `${config.funnelUrl}/claims/barcode/scanner?lang=de`,
    "de-at": `${config.funnelUrl}/claims/barcode/scanner?lang=de-at`,
    "de-ch": `${config.funnelUrl}/claims/barcode/scanner?lang=de-ch`,
    el: `${config.funnelUrl}/claims/barcode/scanner?lang=el`,
    en: `${config.funnelUrl}/claims/barcode/scanner?lang=en`,
    "en-au": `${config.funnelUrl}/claims/barcode/scanner?lang=en-au`,
    "en-ca": `${config.funnelUrl}/claims/barcode/scanner?lang=en-ca`,
    "en-gb": `${config.funnelUrl}/claims/barcode/scanner?lang=en-gb`,
    "en-in": `${config.funnelUrl}/claims/barcode/scanner?lang=en-in`,
    "en-int": `${config.funnelUrl}/claims/barcode/scanner?lang=en-int`,
    "en-ie": `${config.funnelUrl}/claims/barcode/scanner?lang=en-ie`,
    "en-nz": `${config.funnelUrl}/claims/barcode/scanner?lang=en-nz`,
    fi: `${config.funnelUrl}/claims/barcode/scanner?lang=fi`,
    fr: `${config.funnelUrl}/claims/barcode/scanner?lang=fr`,
    "fr-be": `${config.funnelUrl}/claims/barcode/scanner?lang=fr-be`,
    "fr-ca": `${config.funnelUrl}/claims/barcode/scanner?lang=fr-ca`,
    hu: `${config.funnelUrl}/claims/barcode/scanner?lang=hu`,
    it: `${config.funnelUrl}/claims/barcode/scanner?lang=it`,
    nb: `${config.funnelUrl}/claims/barcode/scanner?lang=nb`,
    nl: `${config.funnelUrl}/claims/barcode/scanner?lang=nl`,
    pl: `${config.funnelUrl}/claims/barcode/scanner?lang=pl`,
    "pt-br": `${config.funnelUrl}/claims/barcode/scanner?lang=pt-BR`,
    "pt-pt": `${config.funnelUrl}/claims/barcode/scanner?lang=pt`,
    ro: `${config.funnelUrl}/claims/barcode/scanner?lang=ro`,
    "ro-md": `${config.funnelUrl}/claims/barcode/scanner?lang=ro-md`,
    ru: `${config.funnelUrl}/claims/barcode/scanner?lang=ru`,
    sv: `${config.funnelUrl}/claims/barcode/scanner?lang=sv`,
    "zh-cn": `${config.funnelUrl}/claims/barcode/scanner?lang=zh-CN`,
    es: `${config.funnelUrl}/claims/barcode/scanner?lang=es`,
    fallback: `${config.funnelUrl}/claims/barcode/scanner?lang=en`,
  },

  webappBarcodeUploader: {
    isExternal: true,
    bg: `${config.funnelUrl}/claims/barcode/uploader?lang=bg`,
    cs: `${config.funnelUrl}/claims/barcode/uploader?lang=cs`,
    da: `${config.funnelUrl}/claims/barcode/uploader?lang=da`,
    de: `${config.funnelUrl}/claims/barcode/uploader?lang=de`,
    "de-at": `${config.funnelUrl}/claims/barcode/uploader?lang=de-at`,
    "de-ch": `${config.funnelUrl}/claims/barcode/uploader?lang=de-ch`,
    el: `${config.funnelUrl}/claims/barcode/uploader?lang=el`,
    en: `${config.funnelUrl}/claims/barcode/uploader?lang=en`,
    "en-au": `${config.funnelUrl}/claims/barcode/uploader?lang=en-au`,
    "en-ca": `${config.funnelUrl}/claims/barcode/uploader?lang=en-ca`,
    "en-gb": `${config.funnelUrl}/claims/barcode/uploader?lang=en-gb`,
    "en-in": `${config.funnelUrl}/claims/barcode/uploader?lang=en-in`,
    "en-int": `${config.funnelUrl}/claims/barcode/uploader?lang=en-int`,
    "en-ie": `${config.funnelUrl}/claims/barcode/uploader?lang=en-ie`,
    "en-nz": `${config.funnelUrl}/claims/barcode/uploader?lang=en-nz`,
    fi: `${config.funnelUrl}/claims/barcode/uploader?lang=fi`,
    fr: `${config.funnelUrl}/claims/barcode/uploader?lang=fr`,
    "fr-be": `${config.funnelUrl}/claims/barcode/uploader?lang=fr-be`,
    "fr-ca": `${config.funnelUrl}/claims/barcode/uploader?lang=fr-ca`,
    hu: `${config.funnelUrl}/claims/barcode/uploader?lang=hu`,
    it: `${config.funnelUrl}/claims/barcode/uploader?lang=it`,
    nb: `${config.funnelUrl}/claims/barcode/uploader?lang=nb`,
    nl: `${config.funnelUrl}/claims/barcode/uploader?lang=nl`,
    pl: `${config.funnelUrl}/claims/barcode/uploader?lang=pl`,
    "pt-br": `${config.funnelUrl}/claims/barcode/uploader?lang=pt-BR`,
    "pt-pt": `${config.funnelUrl}/claims/barcode/uploader?lang=pt`,
    ro: `${config.funnelUrl}/claims/barcode/uploader?lang=ro`,
    "ro-md": `${config.funnelUrl}/claims/barcode/uploader?lang=ro-md`,
    ru: `${config.funnelUrl}/claims/barcode/uploader?lang=ru`,
    sv: `${config.funnelUrl}/claims/barcode/uploader?lang=sv`,
    "zh-cn": `${config.funnelUrl}/claims/barcode/uploader?lang=zh-CN`,
    es: `${config.funnelUrl}/claims/barcode/uploader?lang=es`,
    fallback: `${config.funnelUrl}/claims/barcode/uploader?lang=en`,
  },

  careers: {
    isExternal: true,
    fallback: "https://careers.airhelp.com/",
  },
};
